import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Copy, ShieldCheck, BookOpen, ChevronDown, ChevronRight, IndentIncrease, IndentDecrease, Rocket, Save, Image as ImageIcon } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls, MediaPicker } from "./crud";

const lbl = (t) => <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{t}</label>;
const nid = () => Math.random().toString(36).slice(2, 10);
const fmt = (d) => (d ? `${d.slice(0, 10)}, ${d.slice(11, 19)}` : "—");

export const numberClauses = (clauses = [], chapterNum = null) => {
  const counters = [];
  let lastLevel = 1;
  return clauses.map((c) => {
    if ((c.type || "text") !== "text") return { ...c, num: null, indentLevel: lastLevel };
    let lv = Math.max(1, c.level || 1);
    if (lv > counters.length + 1) lv = counters.length + 1;
    counters.length = lv;
    counters[lv - 1] = (counters[lv - 1] || 0) + 1;
    lastLevel = lv;
    const base = counters.slice(0, lv).join(".");
    return { ...c, num: chapterNum ? `${chapterNum}.${base}` : base, indentLevel: lv };
  });
};

const TYPE_LABELS = { text: "TEXTO", note: "NOTA", list: "LISTA", table: "TABLA", image: "IMAGEN" };
const outlineBtn = "inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-[11px] text-slate-600 hover:border-[#5698C7] hover:text-[#5698C7] transition-colors duration-200 disabled:opacity-40";
const miniSel = "border border-slate-200 rounded px-1.5 py-0.5 font-mono-tech text-[9px] tracking-widest text-slate-500 bg-white";

function LangToggle({ lang, setLang }) {
  return (
    <div className="inline-flex border border-slate-200 rounded-md overflow-hidden" data-testid="legal-lang-toggle">
      {["es", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`legal-lang-${l}`}
          className={`px-3 py-1 font-mono-tech text-[10px] tracking-widest transition-colors duration-200 ${lang === l ? "bg-[#5698C7] text-white" : "bg-white text-slate-500 hover:text-[#0B1220]"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function TableEditor({ rows, lang, readOnly, onChange }) {
  const safe = rows?.length ? rows : [[{ es: "", en: "" }, { es: "", en: "" }], [{ es: "", en: "" }, { es: "", en: "" }]];
  const setCell = (r, c, v) => onChange(safe.map((row, ri) => (ri === r ? row.map((cl, ci) => (ci === c ? { ...cl, [lang]: v } : cl)) : row)));
  const cols = safe[0]?.length || 0;
  return (
    <div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full">
          <tbody>
            {safe.map((row, ri) => (
              <tr key={ri} className={ri === 0 ? "bg-slate-50" : ""}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-slate-100 p-1">
                    <input className="w-full text-xs px-1.5 py-1 outline-none bg-transparent" readOnly={readOnly}
                      value={cell?.[lang] || ""} onChange={(e) => setCell(ri, ci, e.target.value)} data-testid={`table-cell-${ri}-${ci}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div className="flex gap-2 mt-2">
          <button onClick={() => onChange([...safe, Array.from({ length: cols }, () => ({ es: "", en: "" }))])} className={miniSel} data-testid="table-add-row">+ FILA</button>
          <button onClick={() => safe.length > 1 && onChange(safe.slice(0, -1))} className={miniSel} data-testid="table-del-row">− FILA</button>
          <button onClick={() => onChange(safe.map((r) => [...r, { es: "", en: "" }]))} className={miniSel} data-testid="table-add-col">+ COLUMNA</button>
          <button onClick={() => cols > 1 && onChange(safe.map((r) => r.slice(0, -1)))} className={miniSel} data-testid="table-del-col">− COLUMNA</button>
        </div>
      )}
    </div>
  );
}

function ImageEditor({ image, lang, readOnly, onChange }) {
  const [picker, setPicker] = useState(false);
  const img = image || {};
  return (
    <div className="flex flex-wrap gap-4 items-start">
      <div className="w-40">
        {img.url ? <img src={mediaUrl(img.url)} alt="" className="w-full rounded-md border border-slate-200" /> :
          <div className="w-full h-24 rounded-md border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300"><ImageIcon size={20} /></div>}
        {!readOnly && <button onClick={() => setPicker(true)} className={outlineBtn + " w-full justify-center mt-2"} data-testid="image-pick-btn"><ImageIcon size={12} /> Elegir imagen</button>}
      </div>
      <div className="flex-1 min-w-[200px] space-y-2">
        <input className={inputCls} placeholder={`Pie de imagen (${lang.toUpperCase()})`} readOnly={readOnly}
          value={img.caption?.[lang] || ""} onChange={(e) => onChange({ ...img, caption: { ...img.caption, [lang]: e.target.value } })} data-testid="image-caption" />
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Código (NC-IMG-XXXX)" readOnly={readOnly}
            value={img.code || ""} onChange={(e) => onChange({ ...img, code: e.target.value })} data-testid="image-code" />
        </div>
        <input className={inputCls} placeholder="© Titular de derechos" readOnly={readOnly}
          value={img.copyright || ""} onChange={(e) => onChange({ ...img, copyright: e.target.value })} data-testid="image-copyright" />
      </div>
      {picker && <MediaPicker onSelect={(url, m) => onChange({ ...img, url, code: img.code || m?.code || "", copyright: img.copyright || m?.copyright || "© Natur Commodities, S.L. — Todos los derechos reservados" })} onClose={() => setPicker(false)} />}
    </div>
  );
}

function ClauseBody({ clause, lang, readOnly, onPatch }) {
  const type = clause.type || "text";
  if (type === "table") return <TableEditor rows={clause.rows} lang={lang} readOnly={readOnly} onChange={(rows) => onPatch({ rows })} />;
  if (type === "image") return <ImageEditor image={clause.image} lang={lang} readOnly={readOnly} onChange={(image) => onPatch({ image })} />;
  return (
    <textarea rows={type === "list" ? 3 : 2} readOnly={readOnly}
      placeholder={type === "list" ? "Un elemento de la lista por línea" : type === "note" ? "Texto sin numerar" : ""}
      className={`${inputCls} ${readOnly ? "bg-slate-50 text-slate-500" : ""}`}
      value={clause.text?.[lang] || ""} onChange={(e) => onPatch({ text: { ...clause.text, [lang]: e.target.value } })} data-testid="clause-text" />
  );
}

function ClauseRow({ clause, lang, readOnly, onPatch, onUp, onDown, onAddAfter, onDelete, canUp, canDown }) {
  const type = clause.type || "text";
  const indent = Math.min((clause.indentLevel || 1) - 1, 6);
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3" style={{ marginLeft: `${indent * 1.6}rem` }} data-testid="clause-row">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {clause.num ? <span className="font-mono-tech text-[10px] bg-[#5698C7]/10 text-[#4587B8] border border-[#5698C7]/20 rounded px-1.5 py-0.5">{clause.num}.</span>
            : <span className="font-mono-tech text-[10px] bg-slate-100 text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">·</span>}
          {readOnly ? <span className="font-mono-tech text-[9px] tracking-widest text-slate-400">{TYPE_LABELS[type]}{type === "text" ? ` · NIVEL ${clause.level || 1}` : ""}</span> : (
            <select className={miniSel} value={type} onChange={(e) => onPatch({ type: e.target.value })} data-testid="clause-type-select">
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
          {type === "text" && !readOnly && <span className="font-mono-tech text-[9px] tracking-widest text-slate-400">NIVEL {clause.level || 1}</span>}
        </div>
        {!readOnly && (
          <div className="flex gap-2 text-slate-400 shrink-0">
            <button onClick={onUp} disabled={!canUp} className="hover:text-[#5698C7] disabled:opacity-30" title="Subir" data-testid="clause-up"><ArrowUp size={13} /></button>
            <button onClick={onDown} disabled={!canDown} className="hover:text-[#5698C7] disabled:opacity-30" title="Bajar" data-testid="clause-down"><ArrowDown size={13} /></button>
            {type === "text" && <>
              <button onClick={() => onPatch({ level: Math.max(1, (clause.level || 1) - 1) })} disabled={(clause.level || 1) <= 1} className="hover:text-[#5698C7] disabled:opacity-30" title="Subir nivel" data-testid="clause-outdent"><IndentDecrease size={13} /></button>
              <button onClick={() => onPatch({ level: (clause.level || 1) + 1 })} className="hover:text-[#5698C7]" title="Bajar nivel (sub-cláusula)" data-testid="clause-indent"><IndentIncrease size={13} /></button>
            </>}
            <button onClick={onAddAfter} className="hover:text-[#5698C7]" title="Añadir bloque debajo" data-testid="clause-add-after"><Plus size={13} /></button>
            <button onClick={onDelete} className="hover:text-red-600" title="Eliminar" data-testid="clause-delete"><Trash2 size={13} /></button>
          </div>
        )}
      </div>
      <ClauseBody clause={clause} lang={lang} readOnly={readOnly} onPatch={onPatch} />
    </div>
  );
}

function ChapterCard({ chapter, index, lang, readOnly, onChange, onUp, onDown, onDuplicate, onDelete, canUp, canDown }) {
  const [open, setOpen] = useState(true);
  const clauses = chapter.clauses || [];
  const numbered = numberClauses(clauses, index + 1);
  const setClauses = (cl) => onChange({ ...chapter, clauses: cl });
  const move = (i, dir) => { const cl = [...clauses]; const [it] = cl.splice(i, 1); cl.splice(i + dir, 0, it); setClauses(cl); };
  const addAt = (i) => { const cl = [...clauses]; cl.splice(i + 1, 0, { id: nid(), type: "text", level: clauses[i]?.level || 1, text: { es: "", en: "" } }); setClauses(cl); };
  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={`chapter-card-${index}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-mono-tech text-[10px] bg-[#5698C7]/10 text-[#4587B8] border border-[#5698C7]/20 rounded px-1.5 py-0.5 shrink-0">{index + 1}</span>
          {readOnly ? <span className="text-sm font-medium text-[#0B1220] truncate">{chapter.title?.[lang] || chapter.title?.es}</span>
            : <input className={inputCls} placeholder="Título del capítulo" value={chapter.title?.[lang] || ""}
                onChange={(e) => onChange({ ...chapter, title: { ...chapter.title, [lang]: e.target.value } })} data-testid={`chapter-title-${index}`} />}
        </div>
        {!readOnly && (
          <div className="flex gap-2.5 text-slate-400 ml-3 shrink-0">
            <button onClick={onUp} disabled={!canUp} className="hover:text-[#5698C7] disabled:opacity-30" data-testid={`chapter-up-${index}`}><ArrowUp size={14} /></button>
            <button onClick={onDown} disabled={!canDown} className="hover:text-[#5698C7] disabled:opacity-30" data-testid={`chapter-down-${index}`}><ArrowDown size={14} /></button>
            <button onClick={onDuplicate} className="hover:text-[#5698C7]" title="Duplicar" data-testid={`chapter-duplicate-${index}`}><Copy size={14} /></button>
            <button onClick={onDelete} className="hover:text-red-600" data-testid={`chapter-delete-${index}`}><Trash2 size={14} /></button>
          </div>
        )}
      </div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 px-4 py-2.5 w-full text-left" data-testid={`chapter-toggle-${index}`}>
        {open ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
        <BookOpen size={13} className="text-[#5698C7]" />
        <span className="text-xs text-slate-600">Cláusulas</span>
        <span className="font-mono-tech text-[10px] bg-slate-100 text-slate-500 rounded px-1.5">{clauses.length}</span>
        {readOnly && <span className="ml-auto font-mono-tech text-[9px] tracking-widest text-slate-400">🔒 SOLO LECTURA</span>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {numbered.map((c, i) => (
            <ClauseRow key={c.id || i} clause={c} lang={lang} readOnly={readOnly}
              onPatch={(patch) => setClauses(clauses.map((x, xi) => (xi === i ? { ...x, ...patch } : x)))}
              onUp={() => move(i, -1)} onDown={() => move(i, 1)} onAddAfter={() => addAt(i)}
              onDelete={() => window.confirm("¿Eliminar este bloque?") && setClauses(clauses.filter((_, xi) => xi !== i))}
              canUp={i > 0} canDown={i < clauses.length - 1} />
          ))}
          {!readOnly && (
            <button onClick={() => addAt(clauses.length - 1)} className={outlineBtn + " w-full justify-center"} data-testid={`chapter-add-clause-${index}`}>
              <Plus size={12} /> Añadir cláusula
            </button>
          )}
          {clauses.length === 0 && readOnly && <p className="text-xs text-slate-400">Sin cláusulas.</p>}
        </div>
      )}
    </div>
  );
}

function PreambleEditor({ blocks = [], lang, readOnly, onChange }) {
  const move = (i, dir) => { const b = [...blocks]; const [it] = b.splice(i, 1); b.splice(i + dir, 0, it); onChange(b); };
  if (readOnly && blocks.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-xl bg-slate-50/40 p-4 space-y-2" data-testid="preamble-section">
      <span className="font-mono-tech text-[10px] tracking-widest text-slate-500 block">PREÁMBULO (SIN NUMERAR — versiones, introducciones)</span>
      {blocks.map((b, i) => (
        <div key={b.id || i} className="flex gap-2 items-start">
          <textarea rows={2} readOnly={readOnly} className={`${inputCls} ${readOnly ? "bg-slate-50 text-slate-500" : "bg-white"}`}
            value={b.text?.[lang] || ""} onChange={(e) => onChange(blocks.map((x, xi) => (xi === i ? { ...x, text: { ...x.text, [lang]: e.target.value } } : x)))} data-testid={`preamble-text-${i}`} />
          {!readOnly && (
            <div className="flex flex-col gap-1.5 text-slate-400 pt-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-[#5698C7] disabled:opacity-30" data-testid={`preamble-up-${i}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="hover:text-[#5698C7] disabled:opacity-30" data-testid={`preamble-down-${i}`}><ArrowDown size={12} /></button>
              <button onClick={() => window.confirm("¿Eliminar bloque del preámbulo?") && onChange(blocks.filter((_, xi) => xi !== i))} className="hover:text-red-600" data-testid={`preamble-delete-${i}`}><Trash2 size={12} /></button>
            </div>
          )}
        </div>
      ))}
      {!readOnly && <button onClick={() => onChange([...blocks, { id: nid(), type: "text", text: { es: "", en: "" } }])} className={outlineBtn} data-testid="preamble-add-btn"><Plus size={12} /> Añadir bloque de preámbulo</button>}
    </div>
  );
}

function ClausesEditor({ chapters = [], onChange, lang, readOnly }) {
  const move = (i, dir) => { const ch = [...chapters]; const [it] = ch.splice(i, 1); ch.splice(i + dir, 0, it); onChange(ch); };
  return (
    <div className="space-y-3">
      {chapters.map((ch, i) => (
        <ChapterCard key={ch.id || i} chapter={ch} index={i} lang={lang} readOnly={readOnly}
          onChange={(nc) => onChange(chapters.map((x, xi) => (xi === i ? nc : x)))}
          onUp={() => move(i, -1)} onDown={() => move(i, 1)}
          onDuplicate={() => onChange([...chapters.slice(0, i + 1), { ...JSON.parse(JSON.stringify(ch)), id: nid() }, ...chapters.slice(i + 1)])}
          onDelete={() => window.confirm("¿Eliminar capítulo y todas sus cláusulas?") && onChange(chapters.filter((_, xi) => xi !== i))}
          canUp={i > 0} canDown={i < chapters.length - 1} />
      ))}
      {!readOnly && (
        <button onClick={() => onChange([...chapters, { id: nid(), title: { es: "", en: "" }, clauses: [{ id: nid(), type: "text", level: 1, text: { es: "", en: "" } }] }])}
          className="w-full border-2 border-dashed border-[#5698C7]/40 rounded-xl py-3 text-[#4587B8] text-xs font-medium hover:bg-[#5698C7]/5 transition-colors duration-200" data-testid="add-chapter-btn">
          <Plus size={13} className="inline mr-1" /> Añadir nuevo capítulo
        </button>
      )}
      {chapters.length === 0 && readOnly && <p className="text-xs text-slate-400">Sin capítulos.</p>}
    </div>
  );
}

export default function LegalDocEditor({ doc, cats, onClose, onSaved }) {
  const isNew = !doc._id;
  const [meta, setMeta] = useState({ slug: doc.slug || "", category_id: doc.category_id || cats[0]?._id || "",
    title: doc.title || { es: "", en: "" }, order: doc.order ?? 0, published: !!doc.published, robots: doc.robots || "noindex,follow" });
  const [body, setBody] = useState({ mode: doc.mode || "html", content: doc.content || { es: "", en: "" }, chapters: doc.chapters || [], preamble: doc.preamble || [] });
  const [current, setCurrent] = useState(doc);
  const [view, setView] = useState(isNew ? "draft" : doc.draft ? "draft" : "active");
  const [versions, setVersions] = useState([]);
  const [lang, setLang] = useState("es");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/legal/documents/${doc._id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
      if (doc.draft) setBody({ mode: doc.draft.mode || "html", content: doc.draft.content || { es: "", en: "" }, chapters: doc.draft.chapters || [], preamble: doc.draft.preamble || [] });
    }
  }, []); // eslint-disable-line

  const snapshot = useMemo(() => versions.find((v) => v._id === view), [versions, view]);
  const editable = isNew || (view === "draft" && !!current.draft);
  const shown = snapshot ? { mode: snapshot.mode || "html", content: snapshot.content || {}, chapters: snapshot.chapters || [], preamble: snapshot.preamble || [] }
    : view === "active" && !isNew ? { mode: current.mode || "html", content: current.content || {}, chapters: current.chapters || [], preamble: current.preamble || [] }
    : body;

  const setBodyD = (b) => { setBody(b); setDirty(true); };

  const reload = async () => {
    const r = await api.get("/admin/legal/documents");
    const fresh = r.data.find((d) => d._id === current._id);
    if (fresh) {
      setCurrent(fresh);
      if (fresh.draft) setBody({ mode: fresh.draft.mode || "html", content: fresh.draft.content || { es: "", en: "" }, chapters: fresh.draft.chapters || [], preamble: fresh.draft.preamble || [] });
    }
    api.get(`/admin/legal/documents/${current._id}/versions`).then((v) => setVersions(v.data)).catch(() => {});
    onSaved(false);
    return fresh;
  };

  const createDraft = async () => {
    try { await api.post(`/admin/legal/documents/${current._id}/draft`); toast.success("Borrador creado"); await reload(); setView("draft"); setDirty(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveDraft = async () => {
    setSaving(true);
    try { await api.put(`/admin/legal/documents/${current._id}/draft`, { data: { ...body, title: meta.title } }); toast.success("Borrador guardado"); setDirty(false); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const deleteDraft = async () => {
    if (!window.confirm("¿Eliminar el borrador? Se perderán los cambios no publicados.")) return;
    await api.delete(`/admin/legal/documents/${current._id}/draft`);
    toast.success("Borrador eliminado"); await reload(); setView("active"); setDirty(false);
  };
  const publish = async () => {
    if (!window.confirm("¿Publicar esta versión? La versión actual quedará protegida como snapshot inmutable.")) return;
    setSaving(true);
    try {
      if (dirty) await api.put(`/admin/legal/documents/${current._id}/draft`, { data: { ...body, title: meta.title } });
      await api.post(`/admin/legal/documents/${current._id}/publish`);
      toast.success("Versión publicada"); setDirty(false); await reload(); setView("active");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveMeta = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/legal/documents", { data: { ...meta, ...body } });
        toast.success("Documento creado"); onSaved(true); return;
      }
      await api.put(`/admin/legal/documents/${current._id}`, { data: meta });
      toast.success("Metadatos guardados"); await reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[92vh] overflow-auto p-6" data-testid="legal-doc-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-display text-sm font-semibold">{isNew ? "Nuevo documento" : meta.title?.es || current.slug}</h3>
            {!isNew && <span className="font-mono-tech text-[10px] tracking-wider bg-[#5698C7]/10 text-[#4587B8] border border-[#5698C7]/30 rounded px-1.5 py-0.5">V{current.version}</span>}
          </div>
          <button onClick={onClose} data-testid="legal-doc-modal-close"><X size={16} className="text-slate-500" /></button>
        </div>

        <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-4 mb-5" data-testid="legal-notice">
          <div className="flex items-center gap-2 text-emerald-800 text-xs font-semibold mb-1"><ShieldCheck size={13} /> Gestor documental — sin IA ni edición automática.</div>
          <p className="text-[11px] text-emerald-700 leading-relaxed">Lo que escribes se guarda tal cual y se publica tal cual. La numeración (1, 1.1, 1.1.1…) es estructural, con niveles ilimitados, y se calcula automáticamente por posición. Las versiones publicadas quedan protegidas por snapshots inmutables con hash SHA-256.</p>
        </div>

        {!isNew && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {lbl("VERSIÓN:")}
              <select className={inputCls.replace("w-full ", "") + " w-auto min-w-[220px]"} value={view} onChange={(e) => setView(e.target.value)} data-testid="legal-version-select">
                <option value="active">{`V${current.version} · ACTIVA · ${current.published_at?.slice(0, 10) || "—"}`}</option>
                {current.draft && <option value="draft">{`BORRADOR · ${current.draft.note || ""}`}</option>}
                {versions.map((v) => <option key={v._id} value={v._id}>{`V${v.version} · SNAPSHOT · ${v.snapshot_at?.slice(0, 10)}`}</option>)}
              </select>
              {view === "active" && <span className="border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-0.5 text-[11px] font-medium">ACTIVA</span>}
              {view === "draft" && <span className="border border-amber-200 bg-amber-50 text-amber-700 rounded-full px-2.5 py-0.5 text-[11px] font-medium">BORRADOR</span>}
              {snapshot && <span className="border border-slate-200 bg-slate-50 text-slate-500 rounded-full px-2.5 py-0.5 text-[11px] font-medium">🔒 SNAPSHOT</span>}
              <div className="flex-1" />
              {!current.draft && <button onClick={createDraft} className={btnCls} data-testid="legal-new-draft-btn"><Plus size={13} /> Nueva versión (borrador)</button>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                <span className="block text-[10px] text-slate-400 mb-1">Creada</span>
                <span className="font-mono-tech text-[11px] text-slate-600 block">{fmt(current.created_at)}</span>
                <span className="text-[10px] text-slate-400">{current.author}</span>
              </div>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                <span className="block text-[10px] text-slate-400 mb-1">Última edición</span>
                <span className="font-mono-tech text-[11px] text-slate-600 block">{fmt(current.draft?.updated_at || current.updated_at)}</span>
                <span className="text-[10px] text-slate-400">{current.draft?.updated_by || current.author}</span>
              </div>
              <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/50">
                <span className="block text-[10px] text-emerald-600 mb-1">Publicada</span>
                <span className="font-mono-tech text-[11px] text-emerald-700 block">{fmt(current.published_at)}</span>
                <span className="text-[10px] text-emerald-600">{current.published_by || current.author}</span>
              </div>
            </div>
            <div className="font-mono-tech text-[10px] text-slate-400 mb-5 break-all">HASH: SHA-256 {current.hash}</div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div>{lbl("SLUG (URL)")}<input className={inputCls} value={meta.slug} disabled={!isNew} onChange={(e) => setMeta({ ...meta, slug: e.target.value })} data-testid="legal-doc-slug" /></div>
          <div>{lbl("CATEGORÍA")}
            <select className={inputCls} value={meta.category_id} onChange={(e) => setMeta({ ...meta, category_id: e.target.value })} data-testid="legal-doc-category">
              {cats.map((c) => <option key={c._id} value={c._id}>{c.name?.es}</option>)}
            </select>
          </div>
          <div>{lbl("ORDEN")}<input type="number" className={inputCls} value={meta.order} onChange={(e) => setMeta({ ...meta, order: Number(e.target.value) })} data-testid="legal-doc-order" /></div>
          <label className="flex items-center gap-2 text-xs text-slate-500 mt-5">
            <input type="checkbox" checked={meta.published} onChange={(e) => setMeta({ ...meta, published: e.target.checked })} data-testid="legal-doc-published" /> Publicado en la web
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <input className={inputCls} placeholder="Título (ES)" value={meta.title?.es || ""} onChange={(e) => setMeta({ ...meta, title: { ...meta.title, es: e.target.value } })} data-testid="legal-doc-title-es" />
          <input className={inputCls} placeholder="Título (EN)" value={meta.title?.en || ""} onChange={(e) => setMeta({ ...meta, title: { ...meta.title, en: e.target.value } })} data-testid="legal-doc-title-en" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 mb-4">
          <div className="inline-flex border border-slate-200 rounded-md overflow-hidden">
            {[["html", "HTML"], ["clauses", "Cláusulas"]].map(([m, t]) => (
              <button key={m} disabled={!editable} onClick={() => setBodyD({ ...body, mode: m })} data-testid={`legal-mode-${m}`}
                className={`px-4 py-1.5 text-[11px] font-medium transition-colors duration-200 disabled:opacity-60 ${shown.mode === m ? "bg-[#5698C7] text-white" : "bg-white text-slate-500 hover:text-[#0B1220]"}`}>
                {t}
              </button>
            ))}
          </div>
          <LangToggle lang={lang} setLang={setLang} />
        </div>

        {!editable && !isNew && (
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-400 mb-3">🔒 SOLO LECTURA — {view === "active" ? "crea un borrador para editar" : "snapshot histórico inmutable"}</p>
        )}

        {shown.mode === "html" ? (
          <div className="mb-5">
            {lbl(`CONTENIDO ${lang.toUpperCase()} (HTML: h2, h3, p, ul...)`)}
            <textarea rows={14} readOnly={!editable} className={`${inputCls} font-mono-tech text-xs ${!editable ? "bg-slate-50 text-slate-500" : ""}`}
              value={shown.content?.[lang] || ""} onChange={(e) => setBodyD({ ...body, content: { ...body.content, [lang]: e.target.value } })} data-testid="legal-doc-content" />
          </div>
        ) : (
          <div className="mb-5 space-y-3">
            <PreambleEditor blocks={shown.preamble} lang={lang} readOnly={!editable} onChange={(preamble) => setBodyD({ ...body, preamble })} />
            <ClausesEditor chapters={shown.chapters} lang={lang} readOnly={!editable} onChange={(chapters) => setBodyD({ ...body, chapters })} />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
          <span className={`text-[11px] ${dirty ? "text-amber-600" : "text-emerald-600"}`} data-testid="legal-save-status">{dirty ? "● Cambios sin guardar" : "✓ Todo guardado"}</span>
          <div className="flex flex-wrap gap-2">
            {isNew ? (
              <button onClick={saveMeta} disabled={saving} className={btnCls} data-testid="legal-doc-save-btn"><Save size={13} /> {saving ? "..." : "CREAR DOCUMENTO"}</button>
            ) : (
              <>
                <button onClick={saveMeta} disabled={saving} className={outlineBtn} data-testid="legal-meta-save-btn"><Save size={12} /> Guardar metadatos</button>
                {current.draft && (
                  <>
                    <button onClick={deleteDraft} className={outlineBtn + " hover:border-red-400 hover:text-red-600"} data-testid="legal-draft-delete-btn"><Trash2 size={12} /> Eliminar borrador</button>
                    <button onClick={saveDraft} disabled={saving || view !== "draft"} className={outlineBtn} data-testid="legal-draft-save-btn"><Save size={12} /> Guardar borrador</button>
                    <button onClick={publish} disabled={saving} className={btnCls} data-testid="legal-publish-btn"><Rocket size={13} /> Publicar versión</button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
